import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import { cn } from "@core-ui/utils/cn";
import type { GesCloudSentEmailCode } from "@ext/enterprise-cloud/GesCloudApi";
import t from "@ext/localization/locale/translate";
import { Button } from "@ui-kit/Button";
import { type ReactNode, useCallback, useState } from "react";
import { GesCloudEmailCodeRequest } from "./GesCloudEmailCodeRequest";
import { GesCloudEmailCodeVerify } from "./GesCloudEmailCodeVerify";
import { GesCloudSignInError, type SignInErrorCode } from "./GesCloudSignInError";
import { GesCloudSignInMethodForm } from "./GesCloudSignInMethodForm";
import { GesCloudSocialSignIn } from "./GesCloudSocialSignIn";

type SignInScreen = "methods" | "email" | "corporate";

interface GesCloudSignInProps {
	className?: string;
	gesCloudUrl?: string;
	header?: ReactNode;
	footer?: ReactNode;
	corporateSignIn?: ReactNode;
	initialSignInError?: SignInErrorCode | null;
}

export const GesCloudSignIn = ({
	className,
	gesCloudUrl: gesCloudUrlOverride,
	header,
	footer,
	corporateSignIn,
	initialSignInError = null,
}: GesCloudSignInProps) => {
	const { url: contextGesCloudUrl } = PageDataContextService.value.conf.enterpriseCloud;
	const gesCloudUrl = gesCloudUrlOverride ?? contextGesCloudUrl;
	const emailSignInGesCloudUrl = gesCloudUrl && URL.canParse(gesCloudUrl) ? gesCloudUrl : null;
	const [screen, setScreen] = useState<SignInScreen>("methods");
	const [email, setEmail] = useState("");
	const [sentCode, setSentCode] = useState<GesCloudSentEmailCode | null>(null);
	const [signInError, setSignInError] = useState(initialSignInError);

	const onCodeSent = useCallback((sentToEmail: string, code: GesCloudSentEmailCode) => {
		setEmail(sentToEmail);
		setSentCode(code);
	}, []);

	const onChangeEmail = useCallback(() => setSentCode(null), []);
	const showMethods = useCallback(() => setScreen("methods"), []);

	if (emailSignInGesCloudUrl && sentCode)
		return (
			<div className={cn("flex flex-col gap-6", className)}>
				<GesCloudEmailCodeVerify
					codeLength={sentCode.codeLength}
					email={email}
					gesCloudUrl={emailSignInGesCloudUrl}
					onBack={onChangeEmail}
					retryAfterSeconds={sentCode.retryAfterSeconds}
				/>
			</div>
		);

	return (
		<div className={cn("flex flex-col gap-6", className)}>
			{header}
			{screen === "methods" && (
				<>
					{signInError && <GesCloudSignInError code={signInError} />}
					<div className="flex flex-col gap-2">
						<GesCloudSocialSignIn gesCloudUrl={gesCloudUrl} onSignInError={setSignInError} />
						{emailSignInGesCloudUrl && (
							<Button
								data-qa="ges-cloud-email-sign-in"
								onClick={() => setScreen("email")}
								startIcon="mail"
								type="button"
								variant="outline"
							>
								{t("enterprise-cloud-guest.buttons.continueWithEmail")}
							</Button>
						)}
						{corporateSignIn && (
							<Button
								data-qa="ges-corporate-sign-in"
								onClick={() => setScreen("corporate")}
								startIcon="building-2"
								type="button"
								variant="outline"
							>
								{t("enterprise-cloud-guest.buttons.continueWithGes")}
							</Button>
						)}
					</div>
					{footer}
				</>
			)}
			{screen === "email" && emailSignInGesCloudUrl && (
				<GesCloudSignInMethodForm onBack={showMethods}>
					<GesCloudEmailCodeRequest
						defaultEmail={email}
						gesCloudUrl={emailSignInGesCloudUrl}
						onCodeSent={onCodeSent}
					/>
				</GesCloudSignInMethodForm>
			)}
			{screen === "corporate" && corporateSignIn && (
				<GesCloudSignInMethodForm onBack={showMethods}>{corporateSignIn}</GesCloudSignInMethodForm>
			)}
		</div>
	);
};
